import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useStripe } from '../contexts/StripeContext';
import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../config/firebase';

// Trial configuration
const TRIAL_DAYS = 7;

export const useUsageTracking = () => {
  const { currentUser } = useAuth();
  const { hasActiveSubscription } = useStripe();
  const [usage, setUsage] = useState({
    searches: 0,
    exports: 0,
    notifications: 0,
    resetDate: null,
    trialStartDate: null,
    trialEndDate: null
  });
  const [loading, setLoading] = useState(true);

  // Check if user is on free tier (trial or no access)
  const isOnFreeTier = !hasActiveSubscription();

  // Check if user is in trial period
  const isInTrial = useCallback(() => {
    if (!usage.trialStartDate) return false;
    const now = new Date();
    const trialEnd = new Date(usage.trialEndDate);
    return now < trialEnd;
  }, [usage.trialStartDate, usage.trialEndDate]);

  // Get trial days remaining
  const getTrialDaysRemaining = useCallback(() => {
    if (!usage.trialEndDate) return 0;
    const now = new Date();
    const trialEnd = new Date(usage.trialEndDate);
    const diffTime = trialEnd - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(0, diffDays);
  }, [usage.trialEndDate]);

  // Load usage data
  const loadUsage = useCallback(async () => {
    if (!currentUser || !db) {
      setLoading(false);
      return;
    }

    try {
      const userDoc = await getDoc(doc(db, 'users', currentUser.uid));

      if (userDoc.exists()) {
        const userData = userDoc.data();
        const usageData = userData.usage || {
          searches: 0,
          exports: 0,
          notifications: 0,
          resetDate: new Date().toISOString(),
          trialStartDate: null,
          trialEndDate: null
        };

        // Initialize trial for new users if not already set
        if (!usageData.trialStartDate && !hasActiveSubscription()) {
          const now = new Date();
          const trialEnd = new Date(
            now.getTime() + TRIAL_DAYS * 24 * 60 * 60 * 1000
          );

          const trialUsage = {
            ...usageData,
            trialStartDate: now.toISOString(),
            trialEndDate: trialEnd.toISOString()
          };

          await updateDoc(doc(db, 'users', currentUser.uid), {
            usage: trialUsage,
            usageUpdatedAt: serverTimestamp()
          });

          setUsage(trialUsage);
        } else {
          setUsage(usageData);
        }
      } else {
        // Initialize usage for new user with trial
        // Use setDoc with merge to create the document if it doesn't exist
        const now = new Date();
        const trialEnd = new Date(
          now.getTime() + TRIAL_DAYS * 24 * 60 * 60 * 1000
        );

        const initialUsage = {
          searches: 0,
          exports: 0,
          notifications: 0,
          resetDate: now.toISOString(),
          trialStartDate: now.toISOString(),
          trialEndDate: trialEnd.toISOString()
        };

        // Use setDoc with merge instead of updateDoc for new users
        await setDoc(
          doc(db, 'users', currentUser.uid),
          {
            usage: initialUsage,
            usageUpdatedAt: serverTimestamp()
          },
          { merge: true }
        );

        setUsage(initialUsage);
      }
    } catch (error) {
      console.error('Error loading usage data:', error);
    } finally {
      setLoading(false);
    }
  }, [currentUser, hasActiveSubscription]);

  // Increment usage for a specific action
  const incrementUsage = useCallback(
    async (action) => {
      if (!currentUser || !db || !isOnFreeTier) return;

      try {
        const newUsage = {
          ...usage,
          [action]: usage[action] + 1
        };

        await updateDoc(doc(db, 'users', currentUser.uid), {
          usage: newUsage,
          usageUpdatedAt: serverTimestamp()
        });

        setUsage(newUsage);
      } catch (error) {
        console.error('Error updating usage:', error);
      }
    },
    [currentUser, usage, isOnFreeTier]
  );

  // Check if user has exceeded limits (trial-only: no limits during trial)
  const hasExceededLimit = useCallback(
    (action) => {
      // While loading, assume no limits to prevent false "trial ended" messages
      if (loading) return false;
      if (!isOnFreeTier) return false;
      if (isInTrial()) return false; // Unlimited during trial
      return true; // No access after trial ends
    },
    [loading, isOnFreeTier, isInTrial]
  );

  // Get remaining usage for an action (trial-only: unlimited during trial)
  const getRemainingUsage = useCallback(
    (action) => {
      // While loading, return Infinity to prevent false "trial ended" messages
      if (loading) return Infinity;
      if (!isOnFreeTier) return Infinity;
      if (isInTrial()) return Infinity; // Unlimited during trial
      return 0; // No access after trial ends
    },
    [loading, isOnFreeTier, isInTrial]
  );

  // Check if user can perform an action (trial-only: unlimited during trial)
  const canPerformAction = useCallback(
    (action) => {
      // While loading, assume access to prevent false "trial ended" messages
      if (loading) return true;
      if (!isOnFreeTier) return true;
      if (isInTrial()) return true; // Unlimited during trial
      return false; // No access after trial ends
    },
    [loading, isOnFreeTier, isInTrial]
  );

  // Load usage on mount
  useEffect(() => {
    loadUsage();
  }, [loadUsage]);

  return {
    usage,
    loading,
    isOnFreeTier,
    isInTrial: isInTrial(),
    getTrialDaysRemaining: getTrialDaysRemaining(),
    incrementUsage,
    hasExceededLimit,
    getRemainingUsage,
    canPerformAction
  };
};
